import React, { useState, useEffect } from 'react';
import { HelpCircle, Loader2, Plus, Minus, MessageCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { FaqItem } from '../types';

const FaqContent = () => {
    const navigate = useNavigate();
    const [faqs, setFaqs] = useState<FaqItem[]>([]);
    const [loading, setLoading] = useState(true);

    // Stato per gestire quale FAQ è aperta
    const [activeIndex, setActiveIndex] = useState<number | null>(null);

    // Palette Colors Constants
    const BRAND_CORAL = "#E84C5B";

    useEffect(() => {
        const fetchFaqs = async () => {
            try {
                const response = await api.get('/content/faqs');
                if (response.data.success && Array.isArray(response.data.data)) {
                    setFaqs(response.data.data);
                }
            } catch (error) {
                console.error("Error fetching FAQs", error);
            } finally {
                setLoading(false);
            }
        };

        fetchFaqs();
    }, []);

    const toggleFaq = (index: number) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center pt-20">
                <Loader2 className="w-10 h-10 text-brand animate-spin" />
            </div>
        );
    }

    return (
        <div className="pt-36 px-6 md:px-8 max-w-4xl mx-auto animate-fade-in pb-24">

            {/* --- HEADER --- */}
            <div className="text-center mb-16">
                <span className="text-brand font-bold text-sm tracking-widest uppercase mb-2 block">Support</span>
                <h1 className="text-4xl md:text-5xl font-serif font-bold text-primary mb-6">Frequently Asked Questions</h1>
                <p className="text-xl text-gray-500 max-w-2xl mx-auto">
                    Everything you need to know about booking and staying with Another Home.
                </p>
            </div>

            {/* --- FAQ LIST (Accordion Style) --- */}
            {faqs.length > 0 ? (
                <div className="space-y-4">
                    {faqs.map((f, i) => {
                        const isOpen = activeIndex === i;
                        return (
                            <div
                                key={i}
                                className={`border rounded-2xl transition-all duration-300 overflow-hidden ${
                                    isOpen ? 'border-brand/20 bg-brand/5 shadow-md' : 'border-gray-200 bg-white hover:border-gray-300'
                                }`}
                            >
                                <button
                                    onClick={() => toggleFaq(i)}
                                    className="w-full flex items-center justify-between p-6 text-left focus:outline-none"
                                >
                                    <h3 className={`font-bold text-lg md:text-xl pr-8 ${isOpen ? 'text-brand' : 'text-primary'}`}>
                                        {f.question}
                                    </h3>
                                    {/* Icona Plus/Minus standardizzata sul Brand */}
                                    <div className={`shrink-0 p-2 rounded-full transition-colors ${isOpen ? 'bg-brand text-white' : 'bg-gray-100 text-gray-500'}`}>
                                        {isOpen ? <Minus className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
                                    </div>
                                </button>

                                {/* Content Wrapper for Animation */}
                                <div
                                    className={`transition-[max-height,opacity] duration-300 ease-in-out overflow-hidden ${
                                        isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                                    }`}
                                >
                                    <div className="px-6 pb-6 pt-0 text-gray-600 leading-relaxed text-base md:text-lg whitespace-pre-line border-t border-transparent">
                                        {f.answer}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="text-center py-16 bg-gray-50 rounded-[2rem] border-2 border-dashed border-gray-200">
                    <HelpCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 font-medium text-lg">No frequently asked questions available at the moment.</p>
                </div>
            )}

            {/* --- CONTACT CTA (STILE AGGIORNATO: TONO SU TONO) --- */}
            <div className="mt-20 bg-white border border-gray-100 shadow-xl rounded-[2.5rem] p-8 md:p-12 text-center relative overflow-hidden">
                <div className="relative z-10">

                    {/* ICONA CORAL SU SFONDO CHIARO (TONO SU TONO) */}
                    <div
                        className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6"
                        style={{ backgroundColor: `${BRAND_CORAL}1A` }} // 10% opacity Coral
                    >
                        <MessageCircle className="w-8 h-8" style={{ color: BRAND_CORAL }} />
                    </div>

                    <h3 className="text-2xl font-bold text-primary mb-3">Still have questions?</h3>
                    <p className="text-gray-500 mb-8 max-w-lg mx-auto">
                        Can't find the answer you're looking for? Our support team is here to help.
                    </p>
                    <button
                        onClick={() => navigate('/contact')}
                        className="bg-primary text-white px-8 py-3 rounded-xl font-bold hover:bg-black transition-colors shadow-lg active:scale-95"
                    >
                        Contact Us
                    </button>
                </div>

                {/* Decoration Circles (Uniformati al Brand) */}
                <div className="absolute top-0 right-0 -mt-10 -mr-10 w-40 h-40 bg-[#E84C5B]/5 rounded-full blur-3xl -z-0"></div>
                <div className="absolute bottom-0 left-0 -mb-10 -ml-10 w-40 h-40 bg-[#E84C5B]/5 rounded-full blur-3xl -z-0"></div>
            </div>

        </div>
    );
};

export default FaqContent;